import type { Map } from "../map";
import {IDefaultLayerView, ILayerView} from "../model/Layer";
import {Stop} from "@esWebAPI/es/model/Directions";
import type DirectionsModel from "@esWebAPI/es/map/DirectionsModel";
import _ from "lodash";

export type FeatureReference = {
    layerId: number,
    featureId: number,
    esSourceId?: any // this should never exist! TOR9-2465
};

export default class RoutingModule {
    /**
     * 
     * @param features 
     * 
     * @returns 
     */
    private getFeatureRefsForLayer(features: any[]): FeatureReference[] {
        const featureReferences = [];

        for (let i = 0; i < features.length; i++) {
            let esSourceId = -1;
            if (features[i].attrs["ES_SOURCE_ID"]) { 
                esSourceId = features[i].attrs["ES_SOURCE_ID"];
            }

            featureReferences.push({
                "layerId": features[i].layerViewId,
                "featureId": features[i].id,
                "esSourceId": esSourceId
            });
        }

        return featureReferences;
    }

    private groupFeatureByLayerView(stops: Stop[]): any {
        const featureIdForLayerId = {};
        const layerIds = _.uniq(stops.map(s => parseInt(s.layerViewId)));
        layerIds.forEach((layerId) => {
            featureIdForLayerId[layerId] = [];
        });

        stops.forEach((stop) => {
            var layerId = parseInt(stop.layerViewId);
            var featureId = parseInt(stop.id);
            featureIdForLayerId[layerId].push(featureId);
        });

        return featureIdForLayerId;
    };

    private getStyleText(style) {
        return style?.defaultStyle?.symbol?.text ||
            style?.text ||
            null;
    }

    /**
     * 
     * @param map 
     * @param layerViewId 
     * @param featureId 
     * @param symbolTypeId 
     * @param stopNumber
     * 
     * @returns 
     */
    private getConditionalStyle(map: Map, layerViewId: number, featureId: number, symbolTypeId: number, stopNumber: number) {
        const RouteSymbol = es.api.symbolType.RouteSymbol;

        const styleText = map.layer.getById(layerViewId).getStyleSettingsObjRef().defaultStyle;
        const text = styleText ? es.util.object.deepCopy(this.getStyleText(styleText)) : null;
        const layerStyleSettings = map.layer.getById(layerViewId).getStyleSettingsObjRef();
        const pinColor = RouteSymbol.WAYPOINT ? 4294554432 : es.api.style.getMainColor(layerStyleSettings.defaultStyle);

        let style: any = {
            "$type": "SymbolStyle",
            "color": pinColor,
            "size": (symbolTypeId === RouteSymbol.WAYPOINT ? 32 : 48),
            // get styler.defaultStyle.text
            "text": text,
            "symbolTypeId": symbolTypeId,
        };

        if (stopNumber > 0) {
            style.symbolFaceText = String(stopNumber);
        }

        if (layerStyleSettings.defaultStyle.$type === "SymbolAndPolygonStyle") {
            style = $.extend(es.util.object.deepCopy(layerStyleSettings.defaultStyle), {"symbolStyle": style});
        }

        return {
            "attrValueCriteria": {
                "$type": "Equal",
                "attr": "ES_ID",
                "value": featureId
            },
            "style": style
        };
    }

    /**
     * 
     * @param layerView 
     * 
     * @returns 
     */
    private hasRouteConditionalStyle(layerView: ILayerView): boolean {
        return layerView.styleSettings.conditionalStyleSettings?.name === "route";
    }

    /**
     * 
     * @param stops 
     * @param style 
     * 
     * @returns 
     */
    private getStopNumber(stops: Stop[], style: any) {
        const RouteSymbol = es.api.symbolType.RouteSymbol;

        switch (style.symbolTypeId) {
            case RouteSymbol.WAYPOINT:
            case RouteSymbol.WAYPOINT_OLD:
                return style.symbolFaceText ? parseInt(style.symbolFaceText) : stops.length;

            case RouteSymbol.START:
                return 0;

            case RouteSymbol.FINISH:
                return stops.length - 1;

            case RouteSymbol.ROUNDTRIP:
                return 0;

            default:
                es.log.error("Unknown Route symbol id", style.symbolTypeId);
                return -1;
        }
    }

    /**
     * 
     * @param map 
     * @param stops 
     * @param roundTrip 
     * @param withOrder 
     * 
     * @returns 
     */
    getLayersConditionalStyle(map: Map, stops: Stop[], roundTrip: boolean, withOrder: boolean) {
        const RouteSymbol = es.api.symbolType.RouteSymbol;
        
        let firstSymbol = RouteSymbol.START;
        let lastSymbol = RouteSymbol.FINISH;

        if (roundTrip) {
            firstSymbol = RouteSymbol.ROUNDTRIP;
            lastSymbol = RouteSymbol.WAYPOINT;
        }

        const layersWithConditions = {};
        _.uniq(stops.map(s => s.layerViewId)).forEach((layerId) => {
            layersWithConditions[layerId] = [];
        });
        stops.forEach((stop, idx, stops) => {
            let symbol;
            let stopNumber = withOrder ? idx : 0;
            if (idx === 0) {
                symbol = firstSymbol;
            } else if (idx === stops.length - 1) {
                symbol = lastSymbol;
                stopNumber = roundTrip ? stopNumber : 0; // if not roundtrip, always put stop as 0;
            } else {
                symbol = RouteSymbol.WAYPOINT;
            }
            const layerId = stop.layerViewId;
            if (layerId) {
                layersWithConditions[layerId].push(this.getConditionalStyle(map, layerId, stop.id, symbol, stopNumber));
            }
        }, this);

        return layersWithConditions;
    }

    /**
     * 
     * @param map 
     * @param stops 
     * @param roundTrip 
     * @param withOrder 
     * 
     * @returns 
     */
    setMapStyles(map: Map, stops: Stop[], roundTrip: boolean, withOrder: boolean): JQueryDeferred<any> {
        const layersWithConditions = this.getLayersConditionalStyle(map, stops, roundTrip, withOrder);
        const usedLayerViewId = _.uniq(stops.map(s => s.layerViewId));

        return es.api.layerView.getByGeoMap(map.meta.getId()).then(function(layerViews) {
            const dfds = layerViews.reduce(function(res, layerView) {
                let needsUpdate = false;
                if (usedLayerViewId.includes(layerView.id)) {
                    layerView.styleSettings.conditionalStyleSettings = {
                        conditionalStyles: layersWithConditions[layerView.id],
                        name: "route"
                    };
                    needsUpdate = true;
                } else {
                    if (layerView.styleSettings.conditionalStyleSettings){
                        needsUpdate = true;
                        delete layerView.styleSettings.conditionalStyleSettings;
                    }
                }

                //TODO the following block can be removed once TOR9-11608 is completed.
                // We want to persist the criteria currently on the layerview rather than overriding with whatever is on server
                if (es.util.isMobile() && map.layer.getLayerViewIds().includes(layerView.id)) {
                    const layerViewOnMapWidget = map.layer.getById(layerView.id).getLayerView();
                    layerView.criteria = layerViewOnMapWidget.criteria;
                    needsUpdate = true;
                }
                //END TODO

                if (needsUpdate) {
                    res.push(es.api.appResources.updateLayerview(layerView, map));
                }
                return res;
            }, []);

            return es.util.deferred.all(dfds);
        });
    }

    /**
     * 
     * @param model 
     * @param map 
     * 
     * @returns 
     */
    getRoute(model: DirectionsModel, map: Map): JQueryDeferred<any> {
        map.eventChannel.fireEvent("onSlowActionStart");
        const requestStops = model.getStops();
        map.popup.clear();

        const request = model.buildRequest(map);
        let routeLayerId: number;
        return es.api.route.createRouteLayerView(request, map.meta.getId()).then((routeLayerView) => {
            // Update the stylers for the defaultLayerViews with conditional stylers and then refresh page
            var inOrder = es.control.Directions.getInOrder(requestStops, routeLayerView.routingResponse.routes[0].waypointOrder);
            routeLayerId = routeLayerView.id;
            return es.map.routing.setMapStyles(map, inOrder, model.isRoundTrip(), true);
        }).then(() => {
            const featureIdForLayerId = this.groupFeatureByLayerView(requestStops);
            var dfds = Object.keys(featureIdForLayerId).map((layerId) => {
                return es.api.feature.getByIds(+layerId, featureIdForLayerId[layerId]);
            });
            return Promise.all(dfds);
        }).then((featuresRS) => {
            es.log.info("TOR9-21309: Building featureRefs from featuresRS, layer count=" + featuresRS.length);
            return featuresRS.flatMap(features => this.getFeatureRefsForLayer(features));
        }).then((refs) => {
            es.log.info("TOR9-21309: Calling updateFeatureReferences for routeLayerId=" + routeLayerId + ", refs count=" + (refs ? refs.length : "null"), refs);
            return Promise.all([
                es.api.route.updateFeatureReferences(routeLayerId, refs),
                es.api.layerInfo.getForLayerView(routeLayerId)
            ]);
        }).then(([updatedRouteLayerView, routeLayerViewInfo]) => {
            return Promise.all([updatedRouteLayerView, routeLayerViewInfo, es.api.geoMap.get(map.meta.getId(), {"noCache": true})]);
        }).then(([updatedRouteLayerView, routeLayerViewInfo, geoMap]) => {
            es.api.getEventChannel().fireEvent("onCreateLayerView", updatedRouteLayerView, routeLayerViewInfo, geoMap.lastModified);
            return es.util.deferred.resolved(updatedRouteLayerView.routingResponse);
        }).fail(function() {
            map.eventChannel.fireEvent("onSlowActionFinish");
        });
    }

    /**
     * 
     * @param model 
     * @param geoMapId 
     * 
     * @returns 
     */
    prepareModel(model: DirectionsModel, geoMapId: number): JQueryDeferred<ILayerView> {
        const stops = model.getStops();
        const start = stops[0];
        const end = stops[stops.length - 1];
        
        var isRoundTrip = model.getModel().roundTrip;
        if (isRoundTrip) {
            end.endAddress = start.startAddress;
            end.endLocation = start.startLocation;
        }

        model.setRoundTrip(isRoundTrip);

        // check if there's already a stop with isArtificialEndStop which can happen when prepareModel is called multiple
        // times e.g. during navigation *away* from the page, see TOR9-20431
        const isAlreadyArtificialEndStop = stops.some(s => s?.isArtificialEndStop === true);

        if (!isRoundTrip &&  !isAlreadyArtificialEndStop) {
            const newStop = {} as Stop; //we need a second point because even thought we have one leg the route has two stops
            newStop.steps = [];
            newStop.idleTime = 0;
            newStop.duration = {value: 0, text: "", withIdleTime: 0};
            newStop.distance = {value: 0, text: ""};
            newStop.endAddress = end.endAddress;
            newStop.endLocation = end.endLocation;
            newStop.isArtificialEndStop = true;

            var stopTimesArray = model.getModel().routingTime.stopsIdleTime;
            if (stopTimesArray.length == stops.length+1) { //just to check that we are applying correctly the idleTime for the last stop
                newStop.idleTime = stopTimesArray[stopTimesArray.length-1];
            }

            stops.push(newStop);
            model.setStops(stops);
        }

        model.setOptimized(model.getModel().optimized);
        /*
        stops.map((stop) => {
            stop.invalid = true;
        });
        */

        return es.api.layerView.getByGeoMap(geoMapId).then((layerViews) => {
            const routeLayerView = layerViews.filter(lv => lv.$type === "RouteLayerView")[0];

            var dfds = layerViews.filter(this.hasRouteConditionalStyle).map((layerView) => {
                return es.map.routing.setStopsInfo(layerView, model.getStops(), model.getFeatureReferences());
            });

            return Promise.all(dfds).then(() => {
                model.getEventChannel().fireEvent("onModelReady");
                return routeLayerView;
            });
        });
    }

    public async setStopsInfo(layerview: ILayerView, stops: Stop[], featureReferences?: FeatureReference[]): Promise<void> {
        if (!layerview.styleSettings.conditionalStyleSettings || !Array.isArray(stops)) {
            return;
        }

        const conditionalStyles = layerview.styleSettings.conditionalStyleSettings.conditionalStyles;

        const featIds = conditionalStyles.map((conditionalStyle) => {
            let style = conditionalStyle.style;
            const featId = conditionalStyle.attrValueCriteria.value;

            if (style.$type === "SymbolAndPolygonStyle") {
                style = style.symbolStyle;
            }

            const stopNum = this.getStopNumber(stops, style);
            if(stops[stopNum]){
                stops[stopNum].featKey = layerview.id + ":" + featId;
            } else {
                stops[stopNum] = {featKey: layerview.id + ":" + featId} as Stop;
            }
            return featId;
        });

        const features = await es.api.feature.getByIds(layerview.id, featIds);
        features.forEach((feature) => {
            if (!feature.geom) {
                // On salesforce, some features can stop having geometry
                // (e.g. opportunity linked to an account change the link to another one with no geocoding information)
                // in this case, treat it as if it was no there...
                return;
            }

            const featKey = feature.layerViewId + ":" + feature.id;
            const stop = stops.get("featKey", featKey) as Stop;

            if (!stop) {
                es.log.warn("directionsRouter : Cannot find stop for key " + featKey);
                return;
            }

            stop.label = es.util.label.render(feature.label);
            let geom = feature.geom;
            if (geom[0] === 'MG') { // it means it's originally a SymbolAndPolygonStyle (i.e. radius analysis result layer)
                geom = geom[1];
            }
            const coordinates = [geom[1], geom[2]];

            stop.geom = ["PT", coordinates[0], coordinates[1]];
            stop.layerViewId = feature.layerViewId;
            stop.id = feature.id;

            if (!stop.startAddress) {
                let address = "";
            if (layerview.$type === "DefaultLayerView" && (layerview as IDefaultLayerView).geoLocationMeta?.$type === "AddressGeoLocationMeta") {
                const attrNames = (layerview as IDefaultLayerView).geoLocationMeta.attrNames;
                for (let i = 0; i < attrNames.length; i++){
                    address += feature.attrs[attrNames[i]] + ", ";
                    }
                    address = address.substring(0, address.length -2); // trim trailing ", "
                }

                stop.startAddress = address;
            }

            if (Array.isArray(featureReferences) && featureReferences.length > 0) {
                const reference = featureReferences.find((ref) => {
                    return ref.layerId === feature.layerViewId && ref.featureId === feature.id;
                });

                if (reference) {
                    stop.invalid = (reference.esSourceId !== -1 && reference.esSourceId !== feature.attrs["ES_SOURCE_ID"]);
                } else {
                    stop.invalid = true;
                }

                if (stop.invalid) {
                    es.log.warn("Invalid stop found", stop, feature, reference);
                }
            } else {
                es.log.warn("TOR9-21309: setStopsInfo - No featureReferences! layerViewId=" + layerview.id + ", featureReferences=" + JSON.stringify(featureReferences) + ". Treating all stops as valid");
            }
        });
    }

    /**
     * 
     * @param value 
     * @param distanceUnit 
     * 
     * @returns 
     */
    convertToDistance(value: number, distanceUnit: string): number {
        if (distanceUnit === "UK" || distanceUnit === "MILE" || distanceUnit === "IMPERIAL") {
            return Math.round(value / 16.0934)/100; // so I get 2 decimals
        } else {
            return Math.round(value / 10)/100; // so I get 2 decimals
        }
    }

    /**
     * 
     * @param value 
     * 
     * @returns 
     */
    convertToDuration(value: number): string {
        var hours = Math.floor(value/ 3600);
        var minutes =  Math.ceil((value % 3600) / 60);
        if (minutes === 60){ // if rounded minutes are 60 we increase one hour and set minutes to 0
            minutes = 0;
            hours = ++hours;
        }
        return hours + " h " + minutes + " min" ;
    }

    public isRoundTrip(map: Map): boolean {

        const conditionalStyles = map.layer.list()
            .filter(lvm =>  this.hasRouteConditionalStyle(lvm.getLayerView()))
            .flatMap(lvm => lvm.getStyleSettingsObjRef().conditionalStyleSettings.conditionalStyles);

        return !conditionalStyles.some(cs =>
            cs.style.symbolTypeId === es.api.symbolType.RouteSymbol.START ||
            cs.style.symbolTypeId === es.api.symbolType.RouteSymbol.FINISH
        );
    }

    public async setStopInfoFromConditionalStyle(map: Map, stops?: Stop[]): Promise<Stop[]> {

        const layers = map.layer.list()
            .filter(lvm =>  this.hasRouteConditionalStyle(lvm.getLayerView()));

        const ret = stops?.length ? stops : layers
            .flatMap(lvm => lvm.getStyleSettingsObjRef().conditionalStyleSettings.conditionalStyles)
            .map(_cs => {
                return {} as Stop;
            })

        const promises = layers.map(lvm =>
            es.map.routing.setStopsInfo(lvm.getLayerView(), ret, undefined)
        );

        await Promise.all(promises);
        return ret;
    }
}
